/**
 * Workspace data layer: queries, mutations, and the render event stream.
 *
 * Two things here are worth explaining.
 *
 * **Renders are not requests, they are jobs.** `POST /renders` returns a queued row and the
 * browser follows an SSE stream. So `useRenderStream` owns an EventSource per running render
 * and closes it the moment the render resolves — a phone that locked mid-render reconnects to
 * a stream that replays the last event, which is why a completed render never leaves a
 * spinner running forever.
 *
 * **The session is one object.** Rooms, photos, masks, renders and the cart all come back
 * together and invalidate together. A workspace where the rail and the canvas disagree about
 * which products are in the cart is worse than one that refetches a few kilobytes.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

import { api } from './api'
import type {
  Cart,
  RenderEvent,
  SearchResponse,
  SlotDefinition,
  WorkspaceMask,
  WorkspacePhoto,
  WorkspaceRender,
  WorkspaceSession,
} from './wstypes'

export const sessionKey = (id: string) => ['workspace', id] as const

export function useWorkspace(sessionId: string | undefined) {
  return useQuery({
    queryKey: sessionKey(sessionId ?? ''),
    queryFn: () => api.get<WorkspaceSession>(`/workspace/sessions/${sessionId}`),
    enabled: Boolean(sessionId),
    staleTime: 5_000,
  })
}

export function useSlots() {
  return useQuery({
    queryKey: ['catalog', 'slots'],
    queryFn: () => api.get<SlotDefinition[]>('/catalog/slots'),
    // The slot registry ships in the image and cannot change under a running session.
    staleTime: Infinity,
  })
}

export function useSlotCoverage() {
  return useQuery({
    queryKey: ['inventory', 'coverage'],
    queryFn: () => api.get<Record<string, number>>('/inventory/coverage'),
    staleTime: 5 * 60_000,
  })
}

export interface SearchArgs {
  slot?: string | null
  q?: string
  finish?: string
  style?: string[]
  priceMax?: number
  widthMin?: number
  widthMax?: number
  limit?: number
}

function searchParams(args: SearchArgs): string {
  const params = new URLSearchParams()
  if (args.slot) params.set('slot', args.slot)
  if (args.q) params.set('q', args.q)
  if (args.finish) params.set('finish', args.finish)
  for (const tag of args.style ?? []) params.append('style', tag)
  if (args.priceMax) params.set('price_max', String(args.priceMax))
  if (args.widthMin) params.set('width_min', String(args.widthMin))
  if (args.widthMax) params.set('width_max', String(args.widthMax))
  params.set('limit', String(args.limit ?? 24))
  return params.toString()
}

export function useProductSearch(args: SearchArgs, enabled = true) {
  const query = searchParams(args)
  return useQuery({
    queryKey: ['inventory', 'search', query],
    queryFn: () => api.get<SearchResponse>(`/inventory/search?${query}`),
    enabled: enabled && Boolean(args.slot || args.q),
    staleTime: 60_000,
  })
}

/**
 * A workspace mutation that refetches the session afterwards.
 *
 * Every write in here changes something the session query already holds — a room, a photo, a
 * mask, a cart line — so the invalidation is the same every time and belongs in one place
 * rather than in each of the seven callers. Forgetting it is the bug it exists to prevent: the
 * request succeeds, the server is right, and the screen keeps showing what it showed before.
 */
function useWorkspaceMutation<TArgs, TResult>(
  sessionId: string,
  fn: (args: TArgs) => Promise<TResult>,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: sessionKey(sessionId) }),
  })
}

export function useAddPhoto(sessionId: string) {
  return useWorkspaceMutation(
    sessionId,
    async ({ roomId, file, source }: { roomId: string; file: Blob; source: string }) => {
      const type = file.type || 'image/jpeg'
      const presigned = await api.post<{ url: string; object_key: string }>(
        `/workspace/sessions/${sessionId}/photos:presign?content_type=${encodeURIComponent(type)}`,
      )
      const put = await fetch(presigned.url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': type },
      })
      if (!put.ok) throw new Error('That photo did not finish uploading. Try again.')

      const { width, height } = await imageSize(file)
      return api.post<WorkspacePhoto>(`/workspace/sessions/${sessionId}/rooms/${roomId}/photos`, {
        object_key: presigned.object_key,
        width,
        height,
        source,
        is_base: true,
      })
    },
  )
}

export function useAddMask(sessionId: string) {
  return useWorkspaceMutation(
    sessionId,
    (body: {
      roomId: string
      photo_id: string
      png_base64: string
      slot?: string | null
      method: string
      confidence?: number
    }) => {
      const { roomId, ...rest } = body
      return api.post<WorkspaceMask>(`/workspace/sessions/${sessionId}/rooms/${roomId}/masks`, rest)
    },
  )
}

export function useUpdateMask(sessionId: string) {
  return useWorkspaceMutation(
    sessionId,
    ({ maskId, ...body }: { maskId: string; slot?: string; label?: string }) =>
      api.patch<WorkspaceMask>(`/workspace/sessions/${sessionId}/masks/${maskId}`, body),
  )
}

export function useDeleteMask(sessionId: string) {
  return useWorkspaceMutation(sessionId, (maskId: string) =>
    api.delete(`/workspace/sessions/${sessionId}/masks/${maskId}`),
  )
}

export function useCreateRender(sessionId: string) {
  return useWorkspaceMutation(
    sessionId,
    (body: {
      room_id: string
      base_photo_id?: string
      selections: { mask_id: string; product_id?: string | null; description?: string }[]
      quality?: 'std' | 'hd'
    }) => api.post<WorkspaceRender>(`/workspace/sessions/${sessionId}/renders`, body),
  )
}

export function useAddCartLine(sessionId: string) {
  return useWorkspaceMutation(
    sessionId,
    (body: {
      room_id?: string
      slot?: string | null
      product_id?: string
      qty?: number
      render_id?: string | null
    }) => api.post<Cart>(`/workspace/sessions/${sessionId}/cart`, body),
  )
}

export function useUpdateCartLine(sessionId: string) {
  return useWorkspaceMutation(
    sessionId,
    ({ lineId, ...body }: { lineId: string; qty?: number; notes?: string }) =>
      api.patch<Cart>(`/workspace/sessions/${sessionId}/cart/${lineId}`, body),
  )
}

export function useDeleteCartLine(sessionId: string) {
  return useWorkspaceMutation(sessionId, (lineId: string) =>
    api.delete<Cart>(`/workspace/sessions/${sessionId}/cart/${lineId}`),
  )
}

export function useCustomRequest(sessionId: string) {
  return useWorkspaceMutation(
    sessionId,
    (body: { room_id?: string; slot?: string | null; description: string }) =>
      api.post<Cart>(`/workspace/sessions/${sessionId}/custom-requests`, body),
  )
}

export function useSendToAndrew(sessionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{ queued: boolean }>(`/workspace/sessions/${sessionId}/send`),
    onSuccess: async () => {
      // The packet is built by a job, so the session flips to `sent` a moment after the call
      // returns. Refetching twice — now and shortly after — is what makes the screen agree
      // with the email the homeowner is about to receive.
      await queryClient.invalidateQueries({ queryKey: sessionKey(sessionId) })
      setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: sessionKey(sessionId) })
      }, 2500)
    },
  })
}

export interface ShareLink {
  id: string
  url: string
  expires_at: string
  views: number
  image_url: string | null
}

export function useCreateShare(sessionId: string) {
  return useMutation({
    mutationFn: (renderId: string) =>
      api.post<ShareLink>(`/share/${sessionId}`, { render_id: renderId }),
  })
}

export function useDeleteMyData(estimateId: string) {
  return useMutation({
    mutationFn: () =>
      api.delete<{ queued: boolean; message: string }>(`/estimates/${estimateId}/data`),
  })
}

export function useRenderStream(sessionId: string, renderId: string | null) {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<'idle' | 'running' | 'done' | 'failed'>('idle')
  const [error, setError] = useState<string | null>(null)
  const sourceRef = useRef<EventSource | null>(null)

  useEffect(() => {
    if (!renderId) {
      setStatus('idle')
      return
    }
    setStatus('running')
    setError(null)

    const source = new EventSource(
      `/api/v1/workspace/sessions/${sessionId}/renders/${renderId}/events`,
      { withCredentials: true },
    )
    sourceRef.current = source

    source.onmessage = (message) => {
      let payload: RenderEvent
      try {
        payload = JSON.parse(message.data) as RenderEvent
      } catch {
        return
      }
      if (payload.event === 'render.done') {
        setStatus('done')
        void queryClient.invalidateQueries({ queryKey: sessionKey(sessionId) })
        source.close()
      } else if (payload.event === 'render.failed') {
        setStatus('failed')
        setError(payload.error ?? 'That preview did not come out.')
        void queryClient.invalidateQueries({ queryKey: sessionKey(sessionId) })
        source.close()
      }
    }
    source.onerror = () => {
      // The server closes the stream after each render resolves and after an idle timeout.
      // Neither is an error worth showing; the session refetch below settles the truth.
      source.close()
      void queryClient.invalidateQueries({ queryKey: sessionKey(sessionId) })
    }

    return () => source.close()
  }, [renderId, sessionId, queryClient])

  const cancel = useCallback(() => {
    sourceRef.current?.close()
    setStatus('idle')
  }, [])

  return { status, error, cancel }
}

async function imageSize(file: Blob): Promise<{ width: number; height: number }> {
  const bitmap = await createImageBitmap(file)
  const size = { width: bitmap.width, height: bitmap.height }
  bitmap.close()
  return size
}

// ------------------------------------------------- server-side segmentation (WS5-04)

/** How long to keep polling before giving up and telling the caller to paint instead. */
const SEGMENT_POLL_MS = 2000
const SEGMENT_ATTEMPTS = 20

export interface ServerSegmentResult {
  status: 'ok' | 'busy' | 'unavailable' | 'timeout' | 'failed' | 'gone' | 'pending'
  mask?: WorkspaceMask
  score?: number
}

/**
 * Segment on the server, for devices that cannot run the model themselves (07 §8, WS5-04).
 *
 * Only called when the local segmenter has reported itself `unavailable`. It is slower — about
 * five seconds against a tenth of that on WebGPU — and it is bounded on the server to one at a
 * time, so `busy` is a normal answer and means "paint it instead", not "try again".
 *
 * Polling rather than SSE on purpose: this fires at most a handful of times in a session, on
 * the devices least able to hold a streaming connection open.
 */
export async function segmentOnServer(
  sessionId: string,
  roomId: string,
  photoId: string,
  points: { x: number; y: number; include: boolean }[],
  slot?: string | null,
): Promise<ServerSegmentResult> {
  const queued = await api.post<{ job_id: string }>(
    `/workspace/sessions/${sessionId}/rooms/${roomId}/masks:segment`,
    { photo_id: photoId, points, slot: slot ?? null },
  )

  for (let attempt = 0; attempt < SEGMENT_ATTEMPTS; attempt += 1) {
    await new Promise((resolve) => setTimeout(resolve, SEGMENT_POLL_MS))
    const result = await api.get<ServerSegmentResult>(
      `/workspace/sessions/${sessionId}/masks:segment/${queued.job_id}`,
    )
    if (result.status !== 'pending') return result
  }
  return { status: 'timeout' }
}
